import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, packTrackToPlayable } from "@/lib/api";

const CatalogContext = createContext(null);
const STALE_MS = 3 * 60 * 1000;

export function CatalogProvider({ children }) {
  const [data, setData] = useState({ tracks: [], packs: [] });
  const [config, setConfig] = useState({});
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  const [progress, setProgress] = useState(0);
  const loadedAt = useRef(0);
  const inflight = useRef(null);

  const load = useCallback(async (initial = false) => {
    if (inflight.current) return inflight.current;
    inflight.current = (async () => {
      if (initial) { setError(false); setProgress(8); }
      const tick = initial ? setInterval(() => setProgress((p) => (p < 88 ? p + Math.max(0.3, (88 - p) / 18) : p)), 150) : null;
      try {
        const cfgP = api.get("/config", { timeout: 15000 }).then((r) => { setConfig(r.data); if (initial) setProgress((p) => Math.max(p, 40)); return r; });
        const catP = api.get("/catalog", { timeout: 15000 })
          .catch(() => api.get("/catalog", { timeout: 10000 }))
          .catch(async () => {
            // Fallback: legacy endpoints (smaller responses)
            const [t, p] = await Promise.all([api.get("/tracks?limit=5000", { timeout: 25000 }), api.get("/packs", { timeout: 25000 })]);
            return { data: { tracks: t.data, packs: p.data } };
          })
          .then((r) => { if (initial) setProgress((p) => Math.max(p, 90)); return r; });
        const [, cat] = await Promise.all([cfgP.catch(() => null), catP]);
        setData({ tracks: cat.data.tracks || [], packs: cat.data.packs || [] });
        loadedAt.current = Date.now();
        if (initial) { clearInterval(tick); setProgress(100); setTimeout(() => setReady(true), 250); }
      } catch (e) {
        if (initial) { clearInterval(tick); setError(true); }
      } finally { inflight.current = null; }
    })();
    return inflight.current;
  }, []);

  useEffect(() => { load(true); }, [load]);

  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === "visible" && Date.now() - loadedAt.current > STALE_MS) load(false); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => { document.removeEventListener("visibilitychange", onFocus); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const value = useMemo(() => {
    const { tracks, packs } = data;
    const pistas = packs.flatMap((pack) => (pack.tracks || []).filter((pt) => !pt.track_id).map((pt) => packTrackToPlayable(pack, pt)));
    const byId = new Map(tracks.map((t) => [t.id, t]));
    const packById = new Map(packs.map((p) => [p.id, p]));
    return {
      ready, error, progress, config, tracks, packs, pistas,
      getTrack: (id) => byId.get(id) || null,
      getPack: (id) => packById.get(id) || null,
      findPista: (id) => { for (const p of packs) { const pt = (p.tracks || []).find((x) => x.id === id); if (pt) return { pack: p, pista: pt }; } return null; },
      refresh: () => load(false),
      retry: () => load(true),
    };
  }, [data, config, ready, error, progress, load]);

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export const useCatalog = () => useContext(CatalogContext);

export const inSection = (t, key) => t.category === key || (t.sections || []).includes(key);
export const similarTo = (track, tracks, limit = 8) => {
  if (!track) return [];
  const same = tracks.filter((t) => t.id !== track.id && track.genre && t.genre === track.genre);
  const rest = tracks.filter((t) => t.id !== track.id && !same.includes(t));
  return [...same, ...rest].slice(0, limit);
};
